import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";

// Bar chart: one bar per reporting year = the facility's mean living wage gap (%).
export default class extends Controller {
  static targets = ["canvas", "showTest"];

  // points: latest calc per year, test runs included; realPoints: the same without test runs.
  static values = {
    points: Array,
    realPoints: Array,
    labels: Object,
  };

  connect() {
    this.tooltip = null;
    this.ro = new ResizeObserver(() => this.draw());
    this.ro.observe(this.canvasTarget);
    this.draw();
  }

  disconnect() {
    this.ro?.disconnect();
    this.removeTooltip();
  }

  removeTooltip() {
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
    }
  }

  toggleTest() {
    this.draw();
    this.dispatch("show-test", {
      detail: { showTest: this.showTestTarget.checked },
    });
  }

  label(key, fallback) {
    return this.labelsValue?.[key] || fallback;
  }

  draw() {
    this.removeTooltip();

    const points = this.showTestTarget.checked
      ? this.pointsValue
      : this.realPointsValue;
    const data = (points || [])
      .filter((d) => d.year != null && d.gapPct != null && !Number.isNaN(d.gapPct))
      .map((d) => ({ ...d, gap: Math.max(Number(d.gapPct), 0) }));

    this.canvasTarget.innerHTML = "";
    const containerW = this.canvasTarget.clientWidth;
    if (containerW < 100) return;

    const BAR_COLOR = "#DE703E";
    const LABEL_COLOR = "#4B5565";
    const GRID_COLOR = "#EEF2F6";
    // Keeps a 0% year visible (and hoverable) instead of collapsing to nothing.
    const MIN_BAR_H = 4;

    const height = 280;
    const formatTick = (v) => `${d3.format(",.0f")(v)}%`;

    const maxGap = d3.max(data, (d) => d.gap) || 0;
    const [yMin, yMax] = d3
      .scaleLinear()
      .domain([0, Math.max(maxGap * 1.15, 10)])
      .nice()
      .domain();

    const margin = { top: 16, right: 24, bottom: 36, left: 48 };
    const innerW = containerW - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3
      .select(this.canvasTarget)
      .append("svg")
      .attr("width", containerW)
      .attr("height", height)
      .attr("role", "img")
      .attr("aria-label", "Mean living wage gap by year");

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    if (data.length === 0) {
      g.append("text")
        .attr("x", innerW / 2)
        .attr("y", innerH / 2)
        .attr("text-anchor", "middle")
        .attr("fill", LABEL_COLOR)
        .attr("font-size", "14px")
        .text(this.label("noData", "No data available"));
      return;
    }

    const yScale = d3.scaleLinear().domain([yMin, yMax]).range([innerH, 0]);

    yScale.ticks(5).forEach((t) => {
      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerW)
        .attr("y1", yScale(t))
        .attr("y2", yScale(t))
        .attr("stroke", GRID_COLOR)
        .attr("stroke-width", 1);
    });

    g.append("g")
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(formatTick).tickSize(0))
      .call((sel) => sel.select(".domain").remove())
      .call((sel) =>
        sel
          .selectAll("text")
          .attr("fill", LABEL_COLOR)
          .attr("font-size", "11px")
      );

    const maxBarWidth = 280;
    const gapBetween = data.length > 1 ? Math.min(48, innerW * 0.08) : 0;
    const rawBandW = (innerW - gapBetween * (data.length - 1)) / data.length;
    const bandW = Math.min(rawBandW, maxBarWidth);
    const totalBandsW = bandW * data.length + gapBetween * (data.length - 1);
    const offsetX = (innerW - totalBandsW) / 2;
    const barX = (i) => offsetX + i * (bandW + gapBetween);
    const barH = (d) => Math.max(innerH - yScale(d.gap), MIN_BAR_H);

    const barLayer = g.append("g").attr("class", "facility-bars");

    barLayer
      .selectAll("rect.bar")
      .data(data)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (_, i) => barX(i))
      .attr("y", (d) => innerH - barH(d))
      .attr("width", bandW)
      .attr("height", barH)
      .attr("rx", 6)
      .attr("fill", BAR_COLOR)
      .style("pointer-events", "none");

    // Full-height hit area so short (or 0%) bars are still easy to hover.
    const bars = barLayer.selectAll("rect.bar").nodes();
    data.forEach((d, i) => {
      barLayer
        .append("rect")
        .attr("class", "bar-hit")
        .attr("x", barX(i))
        .attr("y", 0)
        .attr("width", bandW)
        .attr("height", innerH)
        .attr("fill", "transparent")
        .style("cursor", "pointer")
        .on("mouseenter", () => this.showTooltip(bars[i], d, BAR_COLOR))
        .on("mouseleave", () => this.removeTooltip());
    });

    data.forEach((d, i) => {
      g.append("text")
        .attr("x", barX(i) + bandW / 2)
        .attr("y", innerH + 24)
        .attr("text-anchor", "middle")
        .attr("fill", LABEL_COLOR)
        .attr("font-size", "13px")
        .attr("font-weight", "500")
        .text(this.yearLabel(d));
    });
  }

  yearLabel(point) {
    return point.test
      ? `${point.year} (${this.label("test", "Test")})`
      : String(point.year);
  }

  formatGapPct(gap) {
    const rounded = Math.round(Math.max(Number(gap), 0) * 10) / 10;
    const text =
      rounded % 1 === 0 ? String(Math.trunc(rounded)) : rounded.toFixed(1);
    return `${text}%`;
  }

  formatWorkersBelow(point) {
    const total = Number(point.workersTotal);
    if (!total) return "—";

    const below = Number(point.workersBelow) || 0;
    const pct = Math.round((below / total) * 100);
    return `${below.toLocaleString()} ${this.label("of", "of")} ${total.toLocaleString()} (${pct}%)`;
  }

  bodyTextStyle(sel) {
    return sel
      .style("color", "#FFF")
      .style("font-family", '"Open Sans", sans-serif')
      .style("font-size", "12px")
      .style("font-style", "normal")
      .style("font-weight", "400")
      .style("line-height", "18px");
  }

  createTooltip() {
    this.removeTooltip();

    this.tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "supply-chain-facility-chart-tooltip")
      .style("position", "absolute")
      .style("background", "#1F2937")
      .style("padding", "12px 16px")
      .style("border-radius", "8px")
      .style("pointer-events", "none")
      .style("z-index", "1000")
      .style("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)")
      .style("white-space", "nowrap");

    this.tooltip
      .append("div")
      .style("position", "absolute")
      .style("left", "50%")
      .style("bottom", "-6px")
      .style("transform", "translateX(-50%)")
      .style("width", "0")
      .style("height", "0")
      .style("border-left", "6px solid transparent")
      .style("border-right", "6px solid transparent")
      .style("border-top", "6px solid #1F2937");
  }

  appendTooltipHeader(title, dotColor) {
    const header = this.tooltip
      .append("div")
      .style("display", "flex")
      .style("align-items", "center")
      .style("gap", "8px")
      .style("margin-bottom", "4px");

    header
      .append("span")
      .style("width", "8px")
      .style("height", "8px")
      .style("border-radius", "50%")
      .style("background", dotColor)
      .style("flex-shrink", "0");

    header
      .append("span")
      .style("color", "#FFF")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px")
      .style("font-weight", "700")
      .style("line-height", "18px")
      .text(title);
  }

  appendTooltipRow(label, value) {
    const row = this.bodyTextStyle(
      this.tooltip.append("div").style("padding-left", "16px")
    );
    row.append("span").text(`${label}: `);
    row.append("span").style("font-weight", "700").text(value);
  }

  showTooltip(anchor, point, dotColor) {
    this.createTooltip();

    this.appendTooltipHeader(this.yearLabel(point), dotColor);
    this.appendTooltipRow(
      this.label("meanGap", "Average Living Wage Gap"),
      this.formatGapPct(point.gap)
    );
    this.appendTooltipRow(
      this.label("lwEstimate", "Living wage estimate"),
      point.lwEstimate || "—"
    );
    this.appendTooltipRow(
      this.label("workersBelow", "Workers below living wage"),
      this.formatWorkersBelow(point)
    );

    this.positionTooltip(anchor);
  }

  positionTooltip(anchor) {
    if (!this.tooltip || !anchor) return;

    const rect = anchor.getBoundingClientRect();
    const pageX = rect.left + rect.width / 2 + window.scrollX;
    const pageY = rect.top + window.scrollY;

    const node = this.tooltip.node();
    const w = node.offsetWidth;
    const h = node.offsetHeight;
    const gap = 12;

    let left = pageX - w / 2;
    const top = pageY - h - gap;

    const pad = 8;
    const maxLeft = window.scrollX + document.documentElement.clientWidth - w - pad;
    left = Math.max(window.scrollX + pad, Math.min(left, maxLeft));

    this.tooltip.style("left", `${left}px`).style("top", `${top}px`);
  }
}
